/* eslint-disable @typescript-eslint/no-unused-vars */
import { Chevron, Flex, Text, Alert } from "@gjensidige/builders-components";
import { Check, Close } from "@gjensidige/builders-icons/systems";
import tokens from "@gjensidige/builders-tokens";
import clsx from "clsx";
import React, {
  createElement,
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { 
  RowProps, 
  ExpandableRowProps, 
  INDEXED_ACCESS, 
  Description,
  ColumnState
} from "../../interfaces/Types";
import tracker from '../../tracking';
import styles from "./ComparisonTableDesktop.module.scss";
export const headerCellsElementId = "header-cells";

interface HeaderProps {
  columnHeaders: ColumnHeaderProps[];
  columnStates: ColumnState[];
  isTouchingStepIndicator: boolean;
}

export interface ColumnHeaderProps {
  id: string;
  label: React.ReactNode;
  isHighlighted?: boolean;
  disabledMessage?: string;
  isDisabled?: boolean;
  content?: (isHovered: boolean) => React.ReactNode;
}

export interface IComparisonTableDesktopProps {
  columnHeaders: ColumnHeaderProps[];
  rows: RowProps[];
  columnFooters?: ReactNode[];
  onColumnClick: (id: string) => void;
  hasBadge?: boolean;
  isTouchingStepIndicator: boolean;
  productName?: string;
  id?: string;
  showTableRowsByDefault?: boolean;
}

export function ComparisonTableDesktop(props: IComparisonTableDesktopProps) {
  const [expandedRowId, setExpandedRowId] = useState<string | undefined>();
  const [columnHoverId, setColumnHoverId] = useState<string | undefined>();
  const [showTableRows, setShowTableRows] = useState(true);
  const theadRef = useRef<HTMLTableSectionElement | null>(null);
  const tfootRef = useRef<HTMLTableSectionElement | null>(null);

  let eventListener: null | (() => void) = null;
  // const fullWidthWrapperRef = useCallback(
  //   (fullWidthWrapper: HTMLDivElement | null) => {
  //     if (fullWidthWrapper === null && eventListener) {
  //       window.removeEventListener("resize", eventListener);
  //       eventListener = null;
  //     }
  //     if (fullWidthWrapper !== null) {
  //       const gridLeft = document.getElementById("grid-left");
  //       const lastElementInGridLeft = gridLeft?.lastChild;
  //       const tableWrapper = fullWidthWrapper.closest(`#table-wrapper-1`);

  //       if (
  //         lastElementInGridLeft &&
  //         lastElementInGridLeft instanceof HTMLElement &&
  //         fullWidthWrapper &&
  //         tableWrapper &&
  //         tableWrapper instanceof HTMLElement
  //       ) {
  //         const updateMargin = () => {
  //           const { bottom } = lastElementInGridLeft.getBoundingClientRect();
  //           const { top } = fullWidthWrapper.getBoundingClientRect();
  //           const gridLeftLowestPoint = 64 + bottom;

  //           const existingMargin = parseInt(tableWrapper.style.marginTop) || 0;

  //           if (existingMargin + gridLeftLowestPoint - top > 0) {
  //             tableWrapper.style.marginTop = `${
  //               existingMargin + gridLeftLowestPoint - top
  //             }px`;
  //           } else {
  //             tableWrapper.style.marginTop = "0";
  //           }
  //         };

  //         updateMargin();
  //         window.addEventListener("resize", updateMargin);
  //         eventListener = updateMargin;
  //       }
  //     }
  //   },
  //   []
  // );

  useEffect(() => {
    if (tfootRef.current && theadRef.current) {
      const tfootHeight = tfootRef.current.offsetHeight;
      theadRef.current.style.marginBottom = `${tfootHeight}px`;
    }
  }, []);

  const toggleExpandedRow = (rowId: string) => () => {
    tracker.trackEvent(
      {
        eventAction: expandedRowId === rowId ? 'minimize' : 'expand',
        eventCategory: 'expandable',
        eventKey: 'interact',
        eventLabel: rowId
      }
    )
    setExpandedRowId(expandedRowId === rowId ? undefined : rowId);
  }

  const columnStates: ColumnState[] = props.columnHeaders.map(
    ({ id, isHighlighted = false, isDisabled = false, disabledMessage = "" }) => ({
      isHighlighted,
      isDisabled,
      disabledMessage,
      isHovered: columnHoverId === id,
      setIsHovered: () => !isDisabled && setColumnHoverId(id),
      clearIsHovered: () => setColumnHoverId(undefined),
      onColumnClick: props.onColumnClick
        ? () => props.onColumnClick(id)
        : undefined,
    })
  );

  return (
    <div
      id={`table-wrapper-1`}
      className={clsx(styles.comparisonTableDesktop, styles.table)}
    >
      <div
        className={clsx(styles.tableWrapper, {
          [styles.badgeMargin]: props.hasBadge,
        })}
      >
        {/* <FullWidthWrapper> */}
          <Flex layout={1} gap={"none"} center>
            <table
              aria-label={`comparisonTable: ${props.productName}forsikring`}
              className={styles.table}
            >
              <thead ref={theadRef} className={styles.sticky}>
                <Header
                  columnHeaders={props.columnHeaders}
                  columnStates={columnStates}
                  isTouchingStepIndicator={props.isTouchingStepIndicator}
                />
                {/* <tr>
                  <td></td>
                  <td colSpan={props.columnHeaders.length}>
                    <button
                      className={styles.detailsButton}
                      onClick={() => setShowTableRows(!showTableRows)}
                    >
                      <Chevron open={showTableRows} />
                      <Text size="small" weight={500}>
                        {showTableRows ? "Stäng detaljer" : "Öppna detaljer"}
                      </Text>
                    </button>
                  </td>
                </tr> */}
              </thead>
              <tbody
                // ref={fullWidthWrapperRef}
                className={clsx(styles.rowsContainer, {
                  [styles.showTableRows]: showTableRows,
                })}
              >
                {props.rows.map((row) => (
                  <ExpandableRow
                    key={`row-${row.id}`}
                    isExpanded={row.id === expandedRowId}
                    toggleExpand={toggleExpandedRow(row.id)}
                    columnStates={columnStates}
                    {...row}
                  />
                ))}
              </tbody>
              {props.columnFooters && (
                <tfoot ref={tfootRef}>
                  <Footer
                    columnFooters={props.columnFooters}
                    columnStates={columnStates}
                  />
                </tfoot>
              )}
            </table>
          </Flex>
        {/* </FullWidthWrapper> */}
      </div>
    </div>
  );
}

const Header = ({ columnHeaders, columnStates }: HeaderProps) => (
  <tr id={headerCellsElementId}>
    <th scope="col">
      <div className="visually-hidden">Vad täcker försäkringen?</div>
    </th>
    {columnHeaders.map(({ id, label, content }, index) => {
      const columnState = columnStates[index];
      return (
        <th scope="col" key={id}>
          {createElement(
            columnState.onColumnClick ? "button" : "div",
            {
              key: id,
              onMouseOver: columnState.setIsHovered,
              onMouseLeave: columnState.clearIsHovered,
              onClick: columnState.isDisabled ? () => false : columnState.onColumnClick,
              disabled: columnState.isDisabled,
              className: clsx(styles.columnHeader, {
                [styles.disabled]: columnState.isDisabled,
                [styles.disabledArea]: columnState.isDisabled,
                [styles.highlighted]: columnState.isHighlighted,
                [styles.hover]: columnState.isHovered,
              }),
              "aria-pressed": columnState.isHighlighted,
            },
            (content && content(columnState.isHovered)) || label
          )}
        </th>
      );
    })}
  </tr>
);

interface FooterProps {
  columnFooters: ReactNode[];
  columnStates: ColumnState[];
}

const Footer = ({ columnFooters, columnStates }: FooterProps) => (
  <>
  <tr>
    <td className="visually-hidden">Välj erbjudande</td>
    {columnFooters &&
      columnFooters.map((content, index) => {
        const columnState = columnStates[index];
        return (
          <td
            key={`footer-row-${index}`}
            onMouseOver={columnState.setIsHovered}
            onFocus={columnState.setIsHovered}
            onMouseLeave={columnState.clearIsHovered}
            data-theme={columnState.isHighlighted && "dark"}      
            onClick={columnState.isDisabled ? () => false : columnState.onColumnClick}      
          >
            <div
              className={clsx(styles.footerCell, {
                [styles.disabled]: columnState.isDisabled,
                [styles.disabledArea]: columnState.isDisabled,
                [styles.highlighted]: columnState.isHighlighted,
                [styles.hover]: columnState.isHovered,
              })}
            >
              {content}
            </div>
          </td>
        );
      })}
  </tr>
  <tr>
    <td className="visually-hidden">Disabled message</td>
    {columnFooters &&
      columnFooters.map((content, index) => {
        const columnState = columnStates[index];
        return (
          <td key={index}>
            <div className={styles.footerDisabledMessage}>
              {
                columnState.isDisabled && columnState.disabledMessage && (
                  <Alert
                    severity="informative"
                    variant="outlined"

                  >
                    <Text size="body" 
                      dangerouslySetInnerHTML={{ __html: columnState.disabledMessage }}/>

                  </Alert>
                )
              }
            </div>
          </td>
        );
    })}
  </tr>
  </>
);

const ExpandableRow = ({
  columns,
  id,
  expandableContent,
  label,
  isExpanded,
  columnStates,
  toggleExpand,
}: ExpandableRowProps) => {
  const eventLabel = `comparison-table_les-mer_${label}`.replace(/\s/g, "-");
  
  const createMarkup = (text: string) => {
    return { __html: text };
  }
  const onClickRow = () => {
    if (expandableContent) {
      toggleExpand();
    }
  };

  return (
    <>
      <tr
        onClick={() => onClickRow()}
        className={clsx(styles.row, {
          [styles.isExpandable]: !!expandableContent,
          [styles.isExpanded]: isExpanded,
        })}
      >
        <th scope="row" className={styles.labelWrapper}>
          <button
            onClick={onClickRow}
            aria-controls={expandableContent ? `expandable-${id}` : undefined}
            aria-label={expandableContent ? `${label}` : undefined}
            aria-expanded={expandableContent ? isExpanded : undefined}
            className={styles.expandButton}
          >
            {!!expandableContent && (
              <span className={styles.rowExpandButton}>
                <Chevron
                  className={clsx({ [styles.chevron]: !isExpanded })}
                  open={isExpanded}
                />
              </span>
            )}
            <Text as="span" size="small">
              {label}
            </Text>
          </button>
        </th>
        {columns.map((content: string, columnIndex: number) => {
          const columnState = columnStates[
            columnIndex
          ] as INDEXED_ACCESS<ColumnState>;
          const isChecked = content === "Y";
          const isUnchecked = content === "N";
          const isUnknown = content === "?";
          const cellContent = isChecked ? (
            <>
              <span className="visually-hidden">Täckt</span>
              <Check color={columnState?.isDisabled ? tokens.color.blue400 : ''} />
            </>
          ) : isUnchecked ? (
            <>
              <span className="visually-hidden">Inte täckt</span>
              <Close/>
            </>
          ) : isUnknown ? (
            <>?</>
          ) : (
            <Text size="small">{content}</Text>
          );
          return (
            <td key={`row-cell-${id}-${columnIndex}`}>
              <div
                className={clsx(styles.rowCell, {
                  [styles.isChecked]: !isUnchecked && !isUnknown,
                  [styles.isUnknown]: isUnknown,
                  [styles.highlighted]:
                    columnState?.isHighlighted && !isUnknown,
                  [styles.isDisabled]: columnState?.isDisabled,
                  [styles.disabledArea]: columnState?.isDisabled,
                  [styles.withBackgroundColor]: !isExpanded,
                  [styles.expanded]: isExpanded,
                  [styles.hover]: columnState?.isHovered,
                })}
              >
                {cellContent}
              </div>
            </td>
          );
        })}
      </tr>
      <tr
        id={`expandable-${id}`}
        className={clsx(styles.expandable, { [styles.visible]: isExpanded })}
      >
        {!!expandableContent && (
          <th scope="row" colSpan={columns.length + 1}>
            <div
              className={clsx(styles.expandableRow, {
                [styles.rowExpanded]: isExpanded,
              })}
            > {
              expandableContent.map((description: Description, index: number) => (
                <div key={index}>
                  <Text size="small" weight={700} className={styles.coverageDetails}>
                    {description.title}
                  </Text>
                  {/* <Text size="small" >{description.details}</Text> */}
                  {description.details
                    .split('\n')
                    .map((text: string, index: number) => {
                      return (
                        <Text
                          size="small"
                          className={clsx({[styles.coverageDetails] : !text.trim().length})}
                          key={index}
                          dangerouslySetInnerHTML={createMarkup(text)}
                        />
                      );
                    })}
                </div>
              ))
              }
            </div>
          </th>
        )}
      </tr>
    </>
  );
};
